import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create Context
const PageHeaderContext = createContext(null);

// Default configuration
const defaultConfig = {
  breadcrumbs: [],
  title: '',
  icon: '',
  description: '',
  actionButton: null
};

/**
 * PageHeader Provider
 * Wrap components that need PageHeader in App.js
 */
export const PageHeaderProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);

  // Update configuration
  const updateConfig = useCallback((newConfig) => {
    setConfig(prev => ({
      ...defaultConfig,
      ...newConfig
    }));
  }, []);

  // Reset configuration
  const resetConfig = useCallback(() => {
    setConfig(defaultConfig);
  }, []);

  return (
    <PageHeaderContext.Provider value={{ config, updateConfig, resetConfig }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

/**
 * Hook to get PageHeader configuration
 * Used by PageHeader component to read configuration
 */
export const usePageHeaderConfig = () => {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeaderConfig must be used within PageHeaderProvider');
  }
  return context.config;
};

/**
 * Hook to set PageHeader configuration
 * Used by page components to set their Header configuration
 * 
 * @example
 * // In a page component
 * usePageHeader({
 *   breadcrumbs: [{ label: 'Dashboard', path: '/' }, { label: 'About' }],
 *   title: 'About Us',
 *   icon: 'ℹ️',
 *   description: 'Learn more about this project.',
 *   actionButton: { icon: '✨', label: 'Action', onClick: () => {} }
 * });
 */
export const usePageHeader = (pageConfig) => {
  const context = useContext(PageHeaderContext);
  if (!context) {
    throw new Error('usePageHeader must be used within PageHeaderProvider');
  }

  const { updateConfig, resetConfig } = context;

  // Set configuration on mount/update, reset on unmount
  useEffect(() => {
    if (pageConfig) {
      updateConfig(pageConfig);
    }
    
    return () => {
      resetConfig();
    };
    // Only depend on title to decide if update is needed, avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageConfig?.title, updateConfig, resetConfig]);

  return updateConfig;
};

export default PageHeaderContext;
